import {
  CasButton,
  CasRenderDate,
  CasStackRow,
  CasTable,
  CasToolbar,
} from 'src/compass/shared/components';
import CasLink2 from 'src/compass/shared/components/CasLink/CasLink2';
import getCasTableCellValue from 'src/compass/shared/components/CasTable/getCasTableCellValue';
import { IGeneralObjectType } from 'src/compass/site/util';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import Iconify from 'src/components/iconify';
import HubspotLink from 'src/compass/application/entityMaintenance/HubspotLink';
import CustomerContactsButton from 'src/compass/application/entityMaintenance/CustomerContacts/CustomerContactsButton';
// Direct paths rather than the application barrel: this file is inside it.
import CustomerComposeDialog from 'src/compass/application/messaging/CustomerComposeDialog';
import useSiteProperties from 'src/compass/application/general/hooks/useSiteProperties';
import { PreferredChip } from '../CustomerContacts/PreferredChip';
import {
  contactMethods,
  phoneDigits,
  preferredEmail,
  preferredPhoneDigits,
} from '../CustomerContacts/contactPhones';
import { RenderIgnoreLastTouch } from './RenderIgnoreLastTouch';
import { RenderContactsPhone } from './RenderContactsPhone';

interface IProps {
  entityId: number
  data: IGeneralObjectType[];
  onUpdated: VoidFunction;
}

export default function ContactsTab({ entityId, data, onUpdated }: IProps) {
  const composeDialog = useCasBoolean();
  // Hidden until the Show Customer Send feature flag is on, like the listings' Send and Batch Send.
  const { featureFlags } = useSiteProperties();

  return (
    <>
      {/*
        The same editor SCE Reports opens from its compose window, addressed by customer rather
        than by grower. onUpdated is the dialog's own refetch hook, so the tab redraws with the
        saved Include / preferred values instead of the ones it loaded with.
      */}
      <CasToolbar
        left={
          <>
            {/*
              Send belongs beside the contacts it would go to, and ahead of the editor for them.
              Offered in every context this tab appears in: a tab listing a customer's contacts is
              a place someone may want to write to them, whichever page they reached it from.
              Same icon and wording as SCE Reports' Send, so the two compose windows are
              recognisably the same action.
            */}
            {featureFlags.showCustomerSend && (
              <CasButton
                size="small"
                startIcon={<Iconify icon="ic:outline-message" />}
                onClick={composeDialog.setTrue}
              >
                Send
              </CasButton>
            )}
            <CustomerContactsButton
              entityId={entityId}
              onChanged={onUpdated}
              label="Edit Contacts"
            />
          </>
        }
      />
      {composeDialog.value && (
        <CustomerComposeDialog entityId={entityId} onClose={composeDialog.setFalse} />
      )}
      <CasTable
        data={{ result: data }}
        columns={[
          {
            field: 'name',
            render: ({ row, column }) => {
              const value = getCasTableCellValue({ row, column });
              return (
                <CasStackRow alignItems="center">
                  <HubspotLink id={row.id} recordPath="record/0-1" />
                  {value}
                </CasStackRow>
              );
            },
          },
          {
            field: 'defaultInclude',
            label: 'Include by default on msgs',
            render: ({ row }) => (row?.defaultInclude ? 'Yes' : 'No'),
          },
          {
            field: 'email',
            label: 'Email/Phone',
            render: ({ row }) => {
              const preferredDigits = preferredPhoneDigits(row);
              const preferredAddress = preferredEmail(row);

              // The numbers before the emails, main then additional - the order a contact with no
              // Preferred Channel is messaged in (see contactMethods) - each keeping this tab's own
              // formatting: the mailto: link and the tel:/sms: icons.
              return (
                <div>
                  {contactMethods(row).map((method) =>
                    method.kind === 'email' ? (
                      <CasStackRow key={method.value} alignItems="center">
                        <CasLink2 href={`mailto:${method.value}`}>{method.value}</CasLink2>
                        {method.value === preferredAddress && <PreferredChip />}
                      </CasStackRow>
                    ) : (
                      <RenderContactsPhone
                        key={method.suffix}
                        value={method.value}
                        suffix={method.suffix}
                        preferred={!!preferredDigits && phoneDigits(method.value) === preferredDigits}
                      />
                    )
                  )}
                </div>
              );
            },
          },
          {
            field: 'lastTouchpointDate',
            label: 'Last Touchpoint',
            sx: (row: Record<string, any>) => ({ backgroundColor: row?.ignoreLastTouch && '#ddd' }),
            render: CasRenderDate,
          },
          {
            field: 'lastLRPQuoteOpenedDate',
            label: 'Last LRPQuote Open',
            render: CasRenderDate,
          },
          {
            field: 'ignoreLastTouch',
            label: 'Ignore Touchpoints',
            sx: (row: Record<string, any>) => ({ backgroundColor: row?.ignoreLastTouch && '#ddd' }),
            render: (props) => (
              <RenderIgnoreLastTouch
                {...props}
                entityId={entityId}
                onUpdated={onUpdated}
              />
            ),
          },
        ]}
      />
    </>
  );
}
